import React from "react";
import { Link } from "react-router-dom";
import { Droplets, Phone, Mail, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLang, waLink } from "@/i18n";
import { SERVICE_ORDER, SERVICES, CITIES_META } from "@/seo/content";

const Footer = () => {
  const { t, lang } = useLang();
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = `/#${id}`;
  };

  const legal = [
    { nl: "Privacybeleid", en: "Privacy policy" },
    { nl: "Algemene voorwaarden", en: "Terms & conditions" },
    { nl: "Cookiebeleid", en: "Cookie policy" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-slate-900 px-5 pt-16 pb-8 text-slate-300 md:px-8">
      <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-2 lg:grid-cols-5">
        <div>
          <div className="flex items-center gap-2 font-heading text-xl font-bold text-white">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Droplets className="h-5 w-5" />
            </span>
            MyCleaner
          </div>
          <p className="mt-4 max-w-xs text-sm text-slate-400">{t.footer.tagline}</p>
          <div className="mt-5 flex gap-3">
            {[Facebook, Instagram, Linkedin].map((Icon, i) => (
              <a
                key={i}
                href="#"
                data-testid={`social-link-${i}`}
                aria-label="social"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-slate-300 transition-colors hover:bg-blue-600 hover:text-white"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <h4 className="mb-4 font-semibold text-white">{t.footer.services}</h4>
          <ul className="space-y-2 text-sm">
            {SERVICE_ORDER.map((slug) => (
              <li key={slug}>
                <Link to={`/${slug}`} className="hover:text-blue-400">{SERVICES[slug].label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-semibold text-white">Regio</h4>
          <ul className="space-y-2 text-sm">
            {CITIES_META.map((c) => (
              <li key={c.slug}>
                <Link to={`/${c.slug}`} className="hover:text-blue-400">Schoonmaker {c.city}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-semibold text-white">{t.footer.company}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/blog" className="hover:text-blue-400">Kennisbank</Link></li>
            <li><button onClick={() => go("about")} className="hover:text-blue-400">{t.nav.about}</button></li>
            <li><button onClick={() => go("become-cleaner")} className="hover:text-blue-400">{t.nav.becomeCleaner}</button></li>
            <li><button onClick={() => go("pricing")} className="hover:text-blue-400">{t.nav.pricing}</button></li>
            <li><button onClick={() => go("faq")} className="hover:text-blue-400">FAQ</button></li>
            <li><Link to="/admin" data-testid="footer-admin-link" className="hover:text-blue-400">Admin login</Link></li>
          </ul>
          <ul className="mt-4 space-y-2 text-sm">
            {legal.map((l, i) => (
              <li key={i}><a href="#" className="text-slate-400 hover:text-blue-400">{lang === "en" ? l.en : l.nl}</a></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="mb-4 font-semibold text-white">{t.footer.contact}</h4>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-blue-400" /> +31 6 59 12 85 21</li>
            <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-blue-400" /> info@mycleaner.nl</li>
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-400" /> Amsterdam, NL</li>
          </ul>
          <div className="mt-4 space-y-1 text-xs text-slate-500">
            <p>KvK: 00000000 (placeholder)</p>
            <p>BTW: NL000000000B00 (placeholder)</p>
          </div>
          <a
            data-testid="footer-whatsapp-link"
            href={waLink("Hoi MyCleaner!")}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-white hover:brightness-105"
          >
            WhatsApp
          </a>
        </div>
      </div>

      <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center justify-between gap-3 border-t border-slate-800 pt-6 text-center text-sm text-slate-500 sm:flex-row">
        <span>© {new Date().getFullYear()} MyCleaner. {t.footer.rights}</span>
        <div className="flex gap-4">
          {legal.map((l, i) => (
            <a key={i} href="#" className="hover:text-blue-400">{lang === "en" ? l.en : l.nl}</a>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
